import { ChevronDown } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@core/index";
import enFlag from "@packages/assets/en.png";
import trFlag from "@packages/assets/tr.png";
import deFlag from "@packages/assets/de.png";
import frFlag from "@packages/assets/fr.png";

export type ThemeColor =
  | "red"
  | "orange"
  | "blue"
  | "teal"
  | "cyan"
  | "rose"
  | "stone"
  | "lime"
  | "terra"
  | "purple"
  | "amber"
  | "emerald"
  | "violet"
  | "slate"
  | "yellow"
  | "fuchsia"
  | "zinc"
  | "green"
  | "coral"
  | "mint"
  | "lavender"
  | "sunset"
  | "ocean"
  | "bloom"
  | "cream";

interface OutputLanguageSelectorProps {
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
  themeColor?: ThemeColor;
  gradientClasses?: Record<ThemeColor, string>;
  languages?: Array<{ code: string; name: string; flag: string }>;
}

const OutputLanguageSelector = ({
  selectedLanguage,
  onLanguageChange,
  themeColor,
  gradientClasses,
  languages: customLanguages,
}: OutputLanguageSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Default language options with flags
  const defaultLanguages = [
    { code: "en", name: "English", flag: enFlag },
    { code: "tr", name: "Türkçe", flag: trFlag },
    { code: "de", name: "Deutsch", flag: deFlag },
    { code: "fr", name: "Français", flag: frFlag },
  ];

  // Use custom languages if provided, otherwise use default
  const languages = customLanguages || defaultLanguages;

  const selectedLang =
    languages.find((lang) => lang.code === selectedLanguage) || languages[0];

  return (
    <div className="relative" ref={popoverRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        rounded="lg"
        bgColor="bg-white/50 dark:bg-dark-500 hover:bg-white/70 dark:hover:bg-gray-800/70"
        textColor="text-gray-600 dark:text-gray-300"
        size="md"
        className="flex items-center justify-between gap-2 border border-gray-200 dark:border-dark-400 backdrop-blur-sm px-4 py-3 w-full"
      >
        <div className="flex items-center gap-2">
          <img
            src={selectedLang.flag}
            alt={selectedLang.name}
            className="w-5 h-5 rounded-full object-cover shadow-sm"
          />
          <span className="text-sm font-medium">{selectedLang.name}</span>
        </div>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden z-[150] border border-gray-100 dark:border-gray-700">
          <ul className="py-1">
            {languages.map((lang) => (
              <li key={lang.code}>
                <button
                  onClick={() => {
                    onLanguageChange(lang.code);
                    setIsOpen(false);
                  }}
                  className={`
                    flex items-center gap-2 w-full px-4 py-2 text-sm text-left transition-colors
                    ${
                      lang.code === selectedLanguage
                        ? `bg-gradient-to-r ${gradientClasses ? gradientClasses[themeColor!] : "from-black to-black"} text-white`
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }
                  `}
                >
                  <img
                    src={lang.flag}
                    alt={lang.name}
                    className="w-4 h-4 rounded-full object-cover shadow-sm"
                  />
                  <span className="font-medium">{lang.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OutputLanguageSelector;
